import { useEffect, useMemo, useRef, useState } from "react";
import "@/i18n/copyable-reference.css";
import {
  COPIED_RESET_MS,
  COPYABLE_REFERENCE_CLASS as C,
  copyToClipboard,
  copyableReferenceLabels,
  type CopyableReferenceLabels,
} from "@/i18n/copyable-reference";
import { DEFAULT_ERROR_LOCALE, ERROR_LOCALES, resolveErrorLocale } from "@/i18n/use-error-locale";
import { cn } from "@/lib/utils";

/**
 * React adapter for the design-system CopyableReference (ALL-4857, D636).
 *
 * The markup contract, class names, CSS and the copy routine all come from
 * packages/design-system/error-page/copyable-reference.{ts,css}, synced to
 * src/i18n/ by scripts/sync-error-page.mjs. This file only holds React state
 * for the copied / failed feedback, so the copy logic is not duplicated.
 *
 * Renders nothing when there is no reference: until the Spring change that
 * adds X-Alloy-Support-Reference to every response lands, most responses
 * carry none, and a placeholder would send support after nothing.
 */

/** Labels for the active error-page locale, resolved the same way as the
 * error page: `?locale=`, then the browser language, then English. */
export function useSupportReferenceLabels(): CopyableReferenceLabels {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return useMemo(() => {
    const code = resolveErrorLocale(new URLSearchParams(search));
    const catalog = ERROR_LOCALES[code] ?? ERROR_LOCALES[DEFAULT_ERROR_LOCALE];
    return copyableReferenceLabels(catalog.ui);
  }, [search]);
}

type CopyState = "idle" | "copied" | "failed";

export function CopyableReference({
  reference,
  compact = false,
  labels,
  className,
}: {
  reference: string | null | undefined;
  compact?: boolean;
  labels?: CopyableReferenceLabels;
  className?: string;
}) {
  const localeLabels = useSupportReferenceLabels();
  const l = labels ?? localeLabels;
  const [state, setState] = useState<CopyState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  if (!reference) return null;
  const value = reference;

  async function handleCopy() {
    const ok = await copyToClipboard(value);
    setState(ok ? "copied" : "failed");
    if (resetTimer.current) clearTimeout(resetTimer.current);
    if (ok) resetTimer.current = setTimeout(() => setState("idle"), COPIED_RESET_MS);
  }

  const buttonText = state === "copied" ? l.copyReferenceCopied : compact ? l.copyShort : l.copyReference;
  const statusText = state === "copied" ? l.copiedAnnouncement : state === "failed" ? l.copyFailed : "";

  return (
    <span className={cn(C, `${C}--${compact ? "compact" : "full"}`, className)} data-alloy-ref={value}>
      <span className={`${C}__label`}>{compact ? `${l.referenceShortLabel}:` : l.referenceLabel}</span>
      <code className={`${C}__value`} dir="ltr">{value}</code>
      <button
        type="button"
        className={`${C}__copy`}
        aria-label={l.copyReferenceAria}
        data-copied={state === "copied" ? "true" : undefined}
        onClick={() => void handleCopy()}
      >
        {buttonText}
      </button>
      <span className={`${C}__status`} role="status" aria-live="polite">{statusText}</span>
    </span>
  );
}

/** The localized "Missing capability: <name>" line for access-denied states.
 * Renders nothing when the 403 body named no capability. */
export function MissingCapability({
  capability,
  labels,
  className,
}: {
  capability: string | null | undefined;
  labels?: CopyableReferenceLabels;
  className?: string;
}) {
  const localeLabels = useSupportReferenceLabels();
  if (!capability) return null;
  const l = labels ?? localeLabels;
  const [before, ...rest] = l.missingCapability.split("{capability}");
  return (
    <p className={cn(`${C}-capability`, className)}>
      {before}
      <code dir="ltr">{capability}</code>
      {rest.join("")}
    </p>
  );
}

/** Reference plus capability, the pair every access-denied state shows. */
export function SupportDetails({
  reference,
  capability,
  compact = true,
  className,
}: {
  reference?: string | null;
  capability?: string | null;
  compact?: boolean;
  className?: string;
}) {
  if (!reference && !capability) return null;
  return (
    <div className={cn("mt-2 flex flex-col gap-1", className)}>
      <MissingCapability capability={capability} />
      <CopyableReference reference={reference} compact={compact} />
    </div>
  );
}
